import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CanadaProvinceShortCode, CountryTag, IOrganisation, TimeZone, UsaStateShortCode } from '@bciti/types';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CONFIGURABLE_NOTICE_CHANNELS, NOTICE_CHANNEL_LABELS } from 'src/app/utils/notice-channels';

@Component({
    selector: 'app-organisation-form',
    templateUrl: './organisation-form.component.html',
    styleUrls: ['./organisation-form.component.scss'],
})
export class OrganisationFormComponent implements OnInit, OnDestroy, OnChanges {
    destroy$ = new Subject();

    @Input()
    organisation: FormGroup;

    @Input()
    loading: boolean = false;

    @Input()
    saveLoading: boolean = false;

    @Input()
    displayActions: boolean = true;

    @Output()
    submitForm = new EventEmitter<IOrganisation>();

    timezones: TimeZone[] = Object.values(TimeZone);
    countries: CountryTag[] = Object.values(CountryTag);
    channels = CONFIGURABLE_NOTICE_CHANNELS;
    channelLabels = NOTICE_CHANNEL_LABELS;

    canadaShortCode: CanadaProvinceShortCode[] = Object.values(CanadaProvinceShortCode);
    usaShortCode: UsaStateShortCode[] = Object.values(UsaStateShortCode);

    stateShortCodeOptions: (CanadaProvinceShortCode | UsaStateShortCode)[] = [];

    organisationForm = this.fb.group(
        {
            organisation: this.fb.group({
                name: this.fb.control('', [Validators.required]),
                bcitiId: this.fb.control(0),
                hidden: this.fb.control(true, []),
                timeZone: this.fb.control(undefined),
                address: this.fb.control(undefined),
                countryTag: this.fb.control(undefined),
                stateShortCode: this.fb.control(undefined),
                configuredChannels: this.fb.control([]),
            }),
        },
        { updateOn: 'change' },
    );

    constructor(private fb: FormBuilder) {}

    ngOnInit(): void {
        this.subscribeToCountryTagChanges();
    }

    submitOrganisation() {
        const payload = this.organisationForm.value;

        if (!payload.organisation.bcitiId) {
            payload.organisation.bcitiId = undefined;
        }

        this.submitForm.emit(payload.organisation as IOrganisation);
    }

    getStateShortCodeOptions(selectedCountryTag: string): CanadaProvinceShortCode[] | UsaStateShortCode[] {
        if (selectedCountryTag === CountryTag.CAN) {
            return this.canadaShortCode;
        } else if (selectedCountryTag === CountryTag.USA) {
            return this.usaShortCode;
        }
        return [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes?.organisation?.currentValue) {
            this.organisationForm.patchValue({
                organisation: {
                    ...changes.organisation?.currentValue,
                    stateShortCode: changes.organisation?.currentValue.stateShortCode,
                },
            });
        }
        if (changes?.loading?.currentValue) {
            this.organisationForm.disable();
        }
        if (!changes?.loading?.currentValue) {
            this.organisationForm.reset(this.organisationForm.value);
            this.organisationForm.enable();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private subscribeToCountryTagChanges(): void {
        const countryTagControl = this.organisationForm.get('organisation.countryTag');
        const stateShortCodeControl = this.organisationForm.get('organisation.stateShortCode');

        if (countryTagControl && stateShortCodeControl) {
            countryTagControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedCountryTag: string) => {
                this.stateShortCodeOptions = this.getStateShortCodeOptions(selectedCountryTag);
                const currentValue = stateShortCodeControl.value;
                const hasCurrentValue = this.stateShortCodeOptions.includes(currentValue);
                const defaultValue = hasCurrentValue ? currentValue : this.stateShortCodeOptions[0];

                stateShortCodeControl.setValue(defaultValue);
            });
        }
    }
}
